import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, CreditCard, FileDown, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";

export default function VerifyOrder() {
  const { t } = useT();
  useSeo({ title: "Potwierdzenie zamówienia", path: `${ROUTES.pay}`, noindex: true });
  const [params] = useSearchParams();
  const order = params.get("order");
  const token = params.get("t");
  const W = t.order.wizard;
  const V = t.verify;

  const [state, setState] = useState("loading"); // loading | error | ok
  const [res, setRes] = useState(null);
  const [paying, setPaying] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [expiry, setExpiry] = useState(0);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!order || !token) { setState("error"); return; }
    (async () => {
      try {
        const r = await api.post(`/orders/${order}/verify`, null, { params: { t: token } });
        setRes(r.data);
        setExpiry(Date.now() + (r.data.expires_in || 300) * 1000);
        setState("ok");
      } catch {
        setState("error");
      }
    })();
  }, [order, token]);

  useEffect(() => {
    if (state !== "ok" || !expiry) return undefined;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state, expiry]);

  const mode = res?.mode;
  const payable = mode && mode !== "new_krs_deed";
  const remaining = expiry ? Math.max(0, Math.floor((expiry - nowTs) / 1000)) : 0;
  const expired = expiry > 0 && remaining <= 0;
  const mmss = `${String(Math.floor(remaining / 60))}:${String(remaining % 60).padStart(2, "0")}`;
  const BE = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = `${BE}/api/orders/${order}/contract.pdf?t=${res?.t}`;
  const proformaUrl = `${BE}/api/orders/${order}/proforma.pdf?t=${res?.t}`;

  const payNow = () => { if (res?.pay_url) { setPaying(true); window.location.assign(res.pay_url); } };

  return (
    <div data-testid="verify-order-page" className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Twoje Biuro — strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
          <Link to="/" data-testid="verify-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {t.footer.back_home}
          </Link>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 text-center">
          {state === "loading" ? (
            <>
              <Loader2 data-testid="verify-loading" className="animate-spin mx-auto text-slate-500" size={32} />
              <p className="text-sm text-muted-foreground mt-4">{V.verifying}</p>
            </>
          ) : state === "error" ? (
            <>
              <XCircle size={48} className="mx-auto text-rose-500" />
              <h1 data-testid="verify-error-title" className="font-display font-bold text-2xl mt-5">{V.error_title}</h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{V.error_sub}</p>
            </>
          ) : (
            <>
              <CheckCircle2 size={48} className="mx-auto text-brand-teal" />
              <h1 data-testid="verify-success-title" className="font-display font-bold text-2xl mt-5">
                {mode === "new_krs_deed" ? W.success_deed_title : mode === "new_krs_s24" ? W.success_s24_title : t.order.success_title}
              </h1>
              {mode === "new_krs_deed" ? (
                <div data-testid="verify-success-deed" className="text-sm text-muted-foreground mt-3 leading-relaxed space-y-2">
                  <p>{W.success_deed_sub} <a href="mailto:biuro@twojebiuro.waw.pl" className="font-semibold text-brand-teal underline underline-offset-2">biuro@twojebiuro.waw.pl</a></p>
                  <p>{W.success_deed_sub2}</p>
                </div>
              ) : mode === "new_krs_s24" ? (
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{W.success_s24_sub}</p>
              ) : (
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{t.order.success_sub}</p>
              )}

              {payable && !expired && (
                <div data-testid="verify-window-countdown" className="mt-5 rounded-xl bg-brand-yellow/15 border border-brand-yellow/50 px-4 py-3 text-xs leading-relaxed">
                  {t.order.window_note} <strong className="font-mono">{mmss}</strong>
                </div>
              )}

              {payable && (expired ? (
                <div data-testid="verify-window-expired" className="mt-6 rounded-2xl bg-slate-100 dark:bg-slate-800 p-5 text-sm text-muted-foreground leading-relaxed">
                  {t.order.window_expired}
                </div>
              ) : (
                <>
                  {res?.pay_url && (
                    <div data-testid="verify-pay-box" className="mt-6 rounded-2xl bg-slate-100 dark:bg-slate-800 p-5 text-left">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t.order.pay_title}</p>
                      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-sm">{res.package_name}</span>
                        {res.amount_label && <span data-testid="verify-pay-amount" className="font-display font-bold text-xl">{res.amount_label}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t.order.pay_gross}</p>
                      <button data-testid="verify-pay-now-button" onClick={payNow} disabled={paying}
                        className="mt-4 w-full bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-8 py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2">
                        {paying ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
                        {paying ? t.order.pay_redirect : t.order.pay_now}
                      </button>
                      <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">{t.order.pay_email_note}</p>
                    </div>
                  )}
                  <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
                    {(mode === "existing" || mode === "new_jdg") && (
                      <a data-testid="verify-download-pdf" href={pdfUrl} className="bg-brand-navy text-white font-semibold px-8 py-3 rounded-full text-sm hover:bg-brand-teal transition-colors inline-flex items-center justify-center gap-2">
                        <FileDown size={15} /> {t.order.pdf_download}
                      </a>
                    )}
                    <a data-testid="verify-download-proforma" href={proformaUrl} className="px-6 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors inline-flex items-center justify-center gap-2">
                      <FileDown size={15} /> {t.order.proforma_download}
                    </a>
                  </div>
                </>
              ))}

              <div className="mt-8 flex justify-center">
                <Link to="/" className="inline-flex items-center justify-center bg-brand-teal text-white font-semibold px-8 py-3 rounded-full text-sm hover:bg-brand-tealDark transition-colors">
                  {t.footer.back_home}
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
